import React, {
  useCallback,
  useState,
} from 'react'
import { toast } from 'react-toastify'

import { validate } from 'lib/validation'
import {
  post,
  ApiResponse,
} from 'utils/apis/insuranceLounge'

import InputGroup from 'components/base/Form/InputGroup'
import {
  Type as InputType,
  Value,
} from 'components/base/Form/Input/types'
import Button from 'components/base/Button'
import ButtonRow from 'components/base/Button/Row'
import Text from 'components/base/Text'

import { RSVPFormProps } from './types'

const RSVPForm: React.FC<RSVPFormProps> = ({ location }) => {
  const [
    name,
    setName,
  ] = useState<Value>()
  const [
    emailAddress,
    setEmailAddress,
  ] = useState<Value>()
  const [
    phoneNumber,
    setPhoneNumber,
  ] = useState<Value>()
  const [
    dateAttending,
    setDateAttending,
  ] = useState<Value>()

  const validateForm = useCallback(() => {
    const fields = [
      {
        name: 'name',
        rules: 'required|match:pattern=^[^ ]+ [^ ]+$',
        value: name,
        validationErrorMessage: 'Please enter a valid first and last name.',
      },
      {
        name: 'emailAddress',
        rules: 'required|email',
        value: emailAddress,
        validationErrorMessage: 'Please enter a valid email address.',
      },
      {
        name: 'phoneNumber',
        rules: 'required|phoneNumber',
        value: phoneNumber,
        validationErrorMessage: 'Please enter a valid phone number.',
      },
      {
        name: 'dateAttending',
        rules: 'required',
        value: dateAttending,
        validationErrorMessage: 'Please enter a date that you will be attending.',
      },
    ]
      .map(field => ({
        ...field,
        validation: validate(field.rules, field.value),
      }))

    const firstInvalidField = fields.find(field => !field.validation.isValid)

    if (firstInvalidField) {
      return firstInvalidField.validationErrorMessage
    }

    return undefined
  }, [
    name,
    emailAddress,
    phoneNumber,
    dateAttending,
  ])

  const [
    isSubmitting,
    setIsSubmitting,
  ] = useState(false)

  const [
    isSuccess,
    setIsSuccess,
  ] = useState(false)

  const onSubmit = useCallback(async () => {
    const error = validateForm()

    if (error) {
      toast(error)

      return
    }

    setIsSubmitting(true)

    return post('/contact/rsvp-to-medicare-event', {
      dateAttending,
      emailAddress,
      location,
      name,
      phoneNumber,
    })
      .then((resp: ApiResponse) => {
        if (resp.status !== 200) {
          const message = Object.values(resp.data || {})?.[0]?.[0] || 'Unable to submit the form.'

          throw new Error(message)
        }

        toast('Success! Your RSVP has been submitted.', { className: 'inl-success' })

        setIsSuccess(true)
      }).catch(error => {
        toast(error.message || 'An error has occurred. Please try again.')
      }).finally(() => {
        setIsSubmitting(false)
      })
  }, [
    name,
    emailAddress,
    phoneNumber,
    dateAttending,
    location,
    validateForm,
  ])

  if (isSuccess) {
    return (
      <Text weight='bold' color='BLUE' size='xlarge'>Thank you. You have successfully registered.</Text>
    )
  }

  return (
    <>
      <InputGroup
        type={InputType.text}
        label='First and Last Name'
        isCentered={false}
        onChange={value => setName(value)}
        value={name}
      />
      <InputGroup
        type={InputType.email}
        label='Email Address'
        isCentered={false}
        onChange={value => setEmailAddress(value)}
        value={emailAddress}
      />
      <InputGroup
        type={InputType.tel}
        label='Phone Number'
        isCentered={false}
        onChange={value => setPhoneNumber(value)}
        value={phoneNumber}
      />
      <InputGroup
        type={InputType.text}
        label={'Date You\'re Attending'}
        isCentered={false}
        onChange={value => setDateAttending(value)}
        value={dateAttending}
      />
      <ButtonRow isLeftALigned={true}>
        <Button
          disabled={isSubmitting}
          onClick={(event: React.MouseEvent): void => {
            event.preventDefault()

            onSubmit()
          }}
        >
          {isSubmitting ? 'Sending...' : 'Submit'}
        </Button>
      </ButtonRow>
    </>
  )
}

export default RSVPForm
